import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Truck, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

// Mirrors the `outcome` enum on the ShipStationCallback model. The endpoint answers 200
// to ShipStation for everything except `malformed`/`unauthorized`, so this column is the
// only place a failed-but-acknowledged callback is visible.
const OUTCOME = {
  applied:         { label: "Applied",         cls: "bg-emerald-100 text-emerald-800 border-emerald-200", hint: "Order was updated." },
  duplicate:       { label: "Duplicate",       cls: "bg-slate-100 text-slate-700 border-slate-200",       hint: "Repeat of a notification already applied — nothing changed. This is expected on a re-send." },
  order_not_found: { label: "Order not found", cls: "bg-amber-100 text-amber-800 border-amber-200",       hint: "No order matched the order number ShipStation sent. Acknowledged with 200 so ShipStation doesn't queue a retry." },
  malformed:       { label: "Malformed",       cls: "bg-amber-100 text-amber-800 border-amber-200",       hint: "Request was missing order_number — rejected with 400." },
  unauthorized:    { label: "Unauthorized",    cls: "bg-red-100 text-red-800 border-red-200",             hint: "Credentials did not match PlatformSettings — rejected with 401." },
  error:           { label: "Error",           cls: "bg-red-100 text-red-800 border-red-200",             hint: "Our own failure while applying the update. Acknowledged with 200 so ShipStation doesn't mark the notification failed — but the order was NOT updated." },
};

/**
 * Inbound ShipStation shipnotify callbacks for one order, newest first.
 *
 * Exists because the callback endpoint deliberately answers 200 for outcomes it can't do
 * anything about (unknown order, internal error) — that keeps ShipStation from marking a
 * real shipment notification failed, but it also means the HTTP response no longer tells
 * anyone when something went wrong. This popup is where that lives instead.
 */
export default function ShipStationLogDialog({ open, onClose, orderId, orderNumber }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders/${orderId}/shipstation-logs`);
      setLogs(res.data?.logs || []);
    } catch (e) {
      toast.error("Failed to load ShipStation log");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-blue-600" />
            ShipStation Log {orderNumber ? `· ${orderNumber}` : ""}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>
            {loading ? "Loading…" : `${logs.length} callback${logs.length === 1 ? "" : "s"} recorded`}
          </span>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-400">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-sm">
            No ShipStation callbacks recorded for this order yet.
            <div className="mt-2 text-xs text-slate-400">
              ShipStation only calls back when a label is created or an order is marked shipped,
              and only if the store's “Notify Marketplace” option is enabled.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {logs.map((log) => {
              const meta = OUTCOME[log.outcome] || { label: log.outcome, cls: "bg-slate-100 text-slate-700 border-slate-200", hint: "" };
              const isOpen = !!expanded[log.callback_id];
              return (
                <div key={log.callback_id} className="border rounded-xl p-4 bg-slate-50/60">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${meta.cls}`}>
                        {meta.label}
                      </span>
                      <span className="text-xs text-slate-500">HTTP {log.http_status}</span>
                    </div>
                    <span className="text-xs text-slate-500">{fmt(log.received_at || log.created_at)}</span>
                  </div>

                  {meta.hint && <p className="text-xs text-slate-500 mt-2">{meta.hint}</p>}

                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm mt-3">
                    <KV k="Order Number" v={log.order_number || "—"} />
                    <KV k="Carrier" v={log.carrier ? String(log.carrier).toUpperCase() : "—"} />
                    <KV k="Tracking" v={log.tracking_number || "—"} />
                    <KV k="Service" v={log.service || "—"} />
                  </div>

                  <div className="flex flex-wrap gap-2 mt-3">
                    <Flag on={log.status_changed} label="Status changed" />
                    <Flag on={log.shipment_recorded} label="Shipment recorded" />
                    <Flag on={log.email_dispatched} label="Shipment email sent" />
                  </div>

                  {log.failure_reason && (
                    <div className="mt-3 text-xs bg-red-50 border border-red-200 text-red-800 rounded-lg p-2.5 break-words">
                      <span className="font-semibold">Reason: </span>{log.failure_reason}
                    </div>
                  )}

                  {log.body_summary && (
                    <div className="mt-3">
                      <div className="text-xs font-semibold text-slate-600 mb-1">Shipment details (from ShipStation's XML)</div>
                      <pre className="text-xs bg-white border rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words text-slate-700">
                        {log.body_summary}
                      </pre>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => toggle(log.callback_id)}
                    className="mt-3 text-xs text-blue-600 hover:underline"
                  >
                    {isOpen ? "Hide raw request" : "Show raw request"}
                  </button>

                  {isOpen && (
                    <div className="mt-2 space-y-2">
                      <div>
                        <div className="text-xs font-semibold text-slate-600 mb-1">
                          Query parameters <span className="font-normal text-slate-400">(credentials redacted)</span>
                        </div>
                        <pre className="text-xs bg-white border rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words text-slate-700">
                          {JSON.stringify(log.query || {}, null, 2)}
                        </pre>
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-slate-600 mb-1">
                          Raw body <span className="font-normal text-slate-400">
                            ({log.body_bytes || 0} bytes{log.content_type ? ` · ${log.content_type}` : ""})
                          </span>
                        </div>
                        <pre className="text-xs bg-white border rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words text-slate-700">
                          {log.raw_body_excerpt || "(no body)"}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-3 border-b border-slate-100 py-0.5">
      <span className="text-slate-500">{k}</span>
      <span className="font-medium text-slate-800 text-right break-all">{v}</span>
    </div>
  );
}

function Flag({ on, label }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${on
      ? "bg-blue-50 text-blue-700 border-blue-200"
      : "bg-slate-100 text-slate-400 border-slate-200"}`}>
      {on ? "✓" : "—"} {label}
    </span>
  );
}
